import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Shield,
  Scale,
  Loader2,
  Copy,
  ArrowRight,
  Sparkles,
  FileText,
  CheckCircle2,
  Crown,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

import { useSubscription } from "@/hooks/use-subscription";
import {
  analyzeNdaRedFlags,
  type NdaAnalysis,
  type NdaRedFlag,
} from "@/lib/nda-detector.functions";
import { saveAnalysisAsVersion } from "@/lib/contracts.functions";
import { AvvoWidget } from "@/components/AvvoWidget";

const SAMPLE_NDA = `MUTUAL NON-DISCLOSURE AGREEMENT

This Agreement is entered into by Acme Corp ("Discloser") and Recipient. Recipient agrees to keep ALL information disclosed by Discloser, whether marked confidential or not, in strict confidence for a period of ten (10) years from the date of disclosure. Recipient shall not, for a period of two (2) years following termination, work for any competitor of Discloser or solicit any of Discloser's employees or customers. Recipient agrees to liquidated damages of $50,000 per violation, plus Discloser's attorneys' fees. Any dispute shall be resolved exclusively in the courts of Delaware. Recipient shall return or destroy all materials within 24 hours of demand.`;

function copy(text: string) {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard.");
}

function RiskGauge({ score, level }: { score: number; level: "low" | "medium" | "high" }) {
  const color =
    level === "high" ? "text-destructive" : level === "medium" ? "text-warning" : "text-success";
  const bg =
    level === "high" ? "bg-destructive/10" : level === "medium" ? "bg-warning/10" : "bg-success/10";
  const ring =
    level === "high"
      ? "ring-destructive/30"
      : level === "medium"
        ? "ring-warning/30"
        : "ring-success/30";

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center rounded-2xl p-6 ring-2",
        bg,
        ring,
      )}
    >
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        NDA Risk Score
      </div>
      <div className={cn("mt-1 text-6xl font-black tabular-nums", color)}>{score}</div>
      <div className="text-xs text-muted-foreground">/ 100</div>
      <div className={cn("mt-2 rounded-full px-3 py-0.5 text-xs font-bold uppercase", color)}>
        {level === "high"
          ? "Walk Away Risk"
          : level === "medium"
            ? "Negotiate Heavily"
            : "Generally Fair"}
      </div>
    </div>
  );
}

function FlagCard({ flag }: { flag: NdaRedFlag }) {
  const [open, setOpen] = useState(false);
  const sev =
    flag.severity === "high"
      ? {
          color: "text-destructive",
          bg: "bg-destructive/5",
          border: "border-destructive/30",
          label: "High",
        }
      : flag.severity === "medium"
        ? {
            color: "text-warning",
            bg: "bg-warning/5",
            border: "border-warning/30",
            label: "Medium",
          }
        : {
            color: "text-muted-foreground",
            bg: "bg-muted/40",
            border: "border-border",
            label: "Low",
          };

  return (
    <div className={cn("rounded-2xl border-2 p-5 shadow-card", sev.border, sev.bg)}>
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className={cn("h-4 w-4", sev.color)} />
            <span className={cn("text-[10px] font-bold uppercase tracking-wider", sev.color)}>
              {sev.label} · {flag.category.replace(/-/g, " ")}
            </span>
          </div>
          <h3 className="mt-1 text-base font-bold">{flag.title}</h3>
        </div>
      </div>

      {flag.excerpt && (
        <blockquote className="mb-3 border-l-2 border-muted-foreground/30 pl-3 text-xs italic text-muted-foreground">
          "{flag.excerpt}"
        </blockquote>
      )}

      <p className="text-sm leading-relaxed text-foreground">{flag.whyItMatters}</p>

      <Button
        variant="ghost"
        size="sm"
        className="mt-3 h-8 px-2 text-xs font-bold uppercase tracking-wider"
        onClick={() => setOpen((o) => !o)}
      >
        <Sparkles className="mr-1 h-3 w-3" /> Suggested clarification
      </Button>

      {open && (
        <div className="mt-3 space-y-3 rounded-xl border border-border bg-background p-4">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Ask
              </span>
              <button
                className="text-muted-foreground hover:text-foreground"
                onClick={() => copy(flag.clarification.ask)}
                aria-label="Copy ask"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-sm">{flag.clarification.ask}</p>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Fairer rewrite
              </span>
              <button
                className="text-muted-foreground hover:text-foreground"
                onClick={() => copy(flag.clarification.rewriteSuggestion)}
                aria-label="Copy rewrite"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-sm">{flag.clarification.rewriteSuggestion}</p>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Negotiation script
              </span>
              <button
                className="text-muted-foreground hover:text-foreground"
                onClick={() => copy(flag.clarification.negotiationScript)}
                aria-label="Copy script"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="text-sm italic">{flag.clarification.negotiationScript}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function NdaRedFlagsDetector() {
  const navigate = useNavigate();

  const { isPro } = useSubscription();
  const [text, setText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<NdaAnalysis | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedContractId, setSavedContractId] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (analyzing || text.trim().length < 40) {
      if (text.trim().length < 40) toast.error("Paste a bit more NDA text (40+ characters).");
      return;
    }
    const {
      data: { session: s },
    } = await supabase.auth.getSession();
    if (!s?.access_token) {
      toast.info("Sign in to scan NDAs — your free quota stays with your account.");
      navigate({ to: "/auth", search: { redirect: "/nda-scanner" } as never });
      return;
    }
    setAnalyzing(true);
    setResult(null);
    setAnalysisId(null);
    setSavedContractId(null);
    try {
      const res = await analyzeNdaRedFlags({
        data: { text },
        headers: { Authorization: `Bearer ${s.access_token}` },
      });
      if (!res.ok) {
        if (res.reason === "quota_exceeded") {
          toast.error(
            "You have run out of contract analysis credits. Purchase a credit pack to analyze this document.",
          );
          navigate({ to: "/pricing" });
          return;
        }
        if (res.reason === "rate_limited") {
          toast.error("We're a bit busy — try again in a moment.");
          return;
        }
        toast.error("Couldn't scan that NDA.");
        return;
      }
      setResult(res.result);
      setAnalysisId(res.analysisId);
      setTimeout(() => {
        document.getElementById("nda-results")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message ?? "Scan failed. Try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!isPro) {
      toast.info("Saving to Clause History is a Pro feature.");
      return;
    }
    if (!analysisId) return;
    setSaving(true);
    try {
      const res = await saveAnalysisAsVersion({
        data: {
          analysisId,
          extractedText: text.slice(0, 80000),
          toolSlug: "nda-detector",
        },
      });
      if (!res.ok) {
        toast.error("Couldn't save to Clause History.");
        return;
      }
      setSavedContractId(res.contractId);
      toast.success(`Saved as version ${res.versionNumber}.`);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save to Clause History.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section id="nda-scanner" className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
      <div className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
          <Shield className="h-3.5 w-3.5" /> NDA Red Flags Detector
        </div>
        <h1 className="text-3xl font-black tracking-tight sm:text-5xl">
          Spot the traps in any NDA — before you sign.
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground sm:text-lg">
          Paste your NDA below. We'll grade the risk, flag predatory clauses, and give you exact
          wording to push back. Friendly, fast, and never used to train AI.
        </p>
      </div>

      <div className="rounded-3xl border border-border bg-card p-5 shadow-card sm:p-7">
        <label htmlFor="nda-text" className="text-sm font-bold">
          Paste NDA text
        </label>
        <textarea
          id="nda-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste the full text of the NDA here..."
          className="mt-2 h-56 w-full resize-y rounded-2xl border border-border bg-background p-4 text-sm leading-relaxed shadow-inner focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => setText(SAMPLE_NDA)}
            className="text-xs font-medium text-muted-foreground underline-offset-4 hover:underline"
          >
            Try a sample NDA
          </button>
          <Button
            onClick={handleAnalyze}
            disabled={analyzing}
            variant="hero"
            size="lg"
            className="gap-2"
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Scanning…
              </>
            ) : (
              <>
                <Scale className="h-4 w-4" /> Scan for red flags
              </>
            )}
          </Button>
        </div>
        <p className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
          🔒 Bank-grade encryption · Zero data retention · Not legal advice
        </p>
      </div>

      {result && (
        <div id="nda-results" className="mt-12 scroll-mt-24 space-y-6 animate-fade-in">
          <div className="grid gap-4 lg:grid-cols-3">
            <RiskGauge score={result.overallRiskScore} level={result.riskLevel} />
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card lg:col-span-2">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                The verdict
              </div>
              <p className="mt-2 text-base font-semibold leading-relaxed">{result.summary}</p>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-[11px] font-bold uppercase text-muted-foreground">
                    Party balance
                  </dt>
                  <dd className="capitalize">{result.partyBalance.replace(/-/g, " ")}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase text-muted-foreground">
                    Duration
                  </dt>
                  <dd>{result.keyTerms.duration ?? "Unspecified"}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase text-muted-foreground">
                    Jurisdiction
                  </dt>
                  <dd>{result.keyTerms.jurisdiction ?? "Unspecified"}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-bold uppercase text-muted-foreground">
                    Remedies
                  </dt>
                  <dd>{result.keyTerms.remedies ?? "Unspecified"}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-bold tracking-tight">
              {result.redFlags.length} red flag{result.redFlags.length === 1 ? "" : "s"}
            </h2>
            {analysisId &&
              (savedContractId ? (
                <Button variant="outline" size="sm" className="gap-2" asChild>
                  <Link to="/clause-history/$contractId" params={{ contractId: savedContractId }}>
                    <FileText className="h-4 w-4" /> View in Clause History
                  </Link>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="gap-2"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  Save to Clause History
                  {!isPro && <Lock className="h-3 w-3" />}
                </Button>
              ))}
          </div>

          {result.redFlags.length === 0 ? (
            <div className="rounded-2xl border border-success/30 bg-success/5 p-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
              <p className="mt-2 text-base font-semibold">
                Looks pretty fair! We didn't spot major predatory clauses.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {result.redFlags.map((f) => (
                <FlagCard key={f.id} flag={f} />
              ))}
            </div>
          )}

          {result.missingProtections.length > 0 && (
            <div className="rounded-2xl border border-warning/30 bg-warning/5 p-6">
              <h3 className="mb-3 flex items-center gap-2 text-base font-bold">
                <Shield className="h-4 w-4 text-warning" /> Missing standard protections
              </h3>
              <ul className="space-y-2 text-sm">
                {result.missingProtections.map((m, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-warning" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.riskLevel === "high" && (
            <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-6">
              <h3 className="mb-2 flex items-center gap-2 text-lg font-bold">
                <Scale className="h-5 w-5 text-primary" /> Talk to a real lawyer
              </h3>
              <p className="text-sm text-muted-foreground">
                This NDA has serious red flags. A 30-minute attorney consult could save you
                thousands.
              </p>
              <div className="mt-4">
                <AvvoWidget riskLevel="high" toolSlug="nda-detector" />
              </div>
            </div>
          )}

          {!isPro && (
            <div className="rounded-2xl bg-gradient-to-br from-primary to-primary-glow p-6 text-primary-foreground shadow-lg">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5" />
                <span className="text-xs font-bold uppercase tracking-wider">Pro</span>
              </div>
              <h3 className="mt-2 text-xl font-bold">Track every NDA you sign</h3>
              <p className="mt-1 text-sm opacity-90">
                Save this scan to Clause History, compare versions side-by-side, and export
                lawyer-ready briefings.
              </p>
              <Button asChild variant="secondary" className="mt-4 gap-2">
                <Link to="/pricing">
                  Upgrade to Pro <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          )}

          <p className="text-center text-[11px] uppercase tracking-wider text-muted-foreground">
            Not legal advice. For informational purposes only.
          </p>
        </div>
      )}
    </section>
  );
}
